import type { GoalHolds } from '../types';

/**
 * HTTP mapping for @kk/api-client (the body's error.code wins; this is the fallback by status): 401 → UNAUTHORIZED · 403 → FORBIDDEN (scope violations, e.g. a child
 * token on a parent route) · 404 → NOT_FOUND · 400/422 → VALIDATION · 409 → INVALID_STATE · 429 → RATE_LIMITED ·
 * 5xx → SERVER · fetch failure → NETWORK. Domain codes (EMAIL_TAKEN … NOT_IMPLEMENTED) come from the response body.
 */
export type DataClientErrorCode =
  | 'UNAUTHORIZED'
  | 'FORBIDDEN'
  | 'NOT_FOUND'
  | 'VALIDATION'
  | 'EMAIL_TAKEN'
  | 'WEAK_PASSWORD'
  | 'INVALID_CREDENTIALS'
  | 'RATE_LIMITED'
  | 'PLAN_LIMIT'
  | 'GATE_SCORE'
  | 'GATE_BALANCE'
  | 'INVALID_STATE'
  // the Kredit Quest link: a hand-off that cannot be redeemed (expired, cancelled, or the child already joined), and a
  // link ended on the 18th birthday
  | 'EXPIRED'
  | 'CANCELLED'
  | 'ALREADY_JOINED'
  | 'LINK_ENDED'
  | 'NOT_IMPLEMENTED'
  | 'SERVER'
  | 'NETWORK';

/** Every failure a DataClient or AuthClient raises. Screens branch on `code`, never on message text. */
export class DataClientError extends Error {
  readonly code: DataClientErrorCode;
  readonly details?: unknown;

  constructor(code: DataClientErrorCode, message?: string, details?: unknown) {
    super(message ?? code);
    this.name = 'DataClientError';
    this.code = code;
    this.details = details;
  }
}

export const isDataClientError = (e: unknown): e is DataClientError => e instanceof DataClientError;

// ---- typed `details` the API attaches to specific codes (screens branch on `code`, then read these) ----------------

/**
 * INVALID_STATE on a Quest publish or restore the checker refused (CONTENT_ENGINE §5: "Errors block publishing. There is
 * no override."): each error, where it is, and why.
 */
export interface QuestGateRefusalDetails {
  reason: 'checker';
  issues: Array<{ rule: string; where: string; message: string }>;
}

/** INVALID_CREDENTIALS on parentReentry: tries left before the pad locks. */
export interface WrongPinDetails {
  attemptsLeft: number;
}
/** RATE_LIMITED on parentReentry: when the pad unlocks (RFC 3339). */
export interface PinLockedDetails {
  lockedUntil: string;
}
/** GATE_SCORE on a child's requestRedemption. */
export interface GateScoreDetails {
  needsScore: number;
  score: number;
}
/** GATE_BALANCE on a child's requestRedemption. */
export interface GateBalanceDetails {
  available: number;
  price: number;
  bbToGo: number;
  /** Present when BB saved in the goal are part of the shortfall, so the refusal can name the split (C05). */
  goalHolds?: GoalHolds;
}
/** INVALID_STATE on a child's requestRedemption when a live request already exists. */
export interface AlreadyRequestedDetails {
  redemptionId: string;
  code: string;
}
/** INVALID_STATE on enrollChild when the child already holds a consent for that organization (P20). */
export interface AlreadyEnrolledDetails {
  enrollmentId: string;
}
/**
 * INVALID_STATE on approveRedemption when the gift-card partner failed (declined, or threw). Everything is already
 * undone — the BB are back on hold and the request is back in the parent queue — so the sheet's job is to say why
 * and let the parent confirm again. `refundFailed` is the exception the parent must SEE: the purchase failed AND
 * the refund of it was refused, so the dollars are still with the partner; no `refund` row was written and
 * `refundFailureReason` is also stored on the order (P13d).
 */
export interface GiftcardPurchaseFailedDetails {
  orderId: string;
  reason: string;
  refundFailed?: boolean;
  refundFailureReason?: string;
}

/**
 * FORBIDDEN on any family-changing write by a grown-up over the free limit (M05). `canAct` is the entitled
 * parent's first name and is what the M05 banner prints — the 403 names who can act, so nothing is hardcoded.
 */
export interface ReadOnlyDetails {
  readOnly: true;
  canAct?: string;
}
/**
 * PLAN_LIMIT while a family is over the child limit with no choice made (M02). Everything but the two ways out
 * — the plan read and the chooser — answers with this until M03 is saved.
 */
export interface PlanLockedDetails {
  locked: true;
  children?: number;
  childLimit?: number;
  grownups?: number;
  grownupLimit?: number;
  chooseChildren?: number;
}

const detailsOf = (e: unknown, code: DataClientErrorCode): Record<string, unknown> | null =>
  isDataClientError(e) && e.code === code && typeof e.details === 'object' && e.details !== null
    ? (e.details as Record<string, unknown>)
    : null;

/** The tries left on a wrong PIN, else null. */
export function attemptsLeftOf(e: unknown): number | null {
  const d = detailsOf(e, 'INVALID_CREDENTIALS');
  return d && typeof d.attemptsLeft === 'number' ? d.attemptsLeft : null;
}
/** When a locked PIN pad reopens, else null. */
export function lockedUntilOf(e: unknown): string | null {
  const d = detailsOf(e, 'RATE_LIMITED');
  return d && typeof d.lockedUntil === 'string' ? d.lockedUntil : null;
}
/** The live request that blocks a second Get it, else null. */
export function alreadyRequestedOf(e: unknown): AlreadyRequestedDetails | null {
  const d = detailsOf(e, 'INVALID_STATE');
  return d && typeof d.redemptionId === 'string' && typeof d.code === 'string'
    ? { redemptionId: d.redemptionId, code: d.code }
    : null;
}

/**
 * The savings goal behind a GATE_BALANCE, else null: the split C05's refusal names ("400 of your Bamboo Bucks are
 * saving for Lego set. Take 50 out to get this."). Null for a plain shortfall, where the goal is not in the way.
 */
export function goalHoldsOf(e: unknown): GoalHolds | null {
  const g = detailsOf(e, 'GATE_BALANCE')?.goalHolds as Record<string, unknown> | undefined;
  if (!g || typeof g.goalName !== 'string') return null;
  const nums = ['saved', 'bbInGoal', 'bbToEarn', 'bbToSpend'] as const;
  if (!nums.every((k) => typeof g[k] === 'number')) return null;
  return {
    goalName: g.goalName,
    saved: g.saved as number,
    bbInGoal: g.bbInGoal as number,
    bbToEarn: g.bbToEarn as number,
    bbToSpend: g.bbToSpend as number,
  };
}

/** The live consent that blocks a second join for the same child, else null. */
export function alreadyEnrolledOf(e: unknown): AlreadyEnrolledDetails | null {
  const d = detailsOf(e, 'INVALID_STATE');
  return d && typeof d.enrollmentId === 'string' ? { enrollmentId: d.enrollmentId } : null;
}

/** The failed gift-card order behind a refused confirm, else null. */
export function giftcardPurchaseFailedOf(e: unknown): GiftcardPurchaseFailedDetails | null {
  const d = detailsOf(e, 'INVALID_STATE');
  if (!d || typeof d.orderId !== 'string' || typeof d.reason !== 'string') return null;
  return {
    orderId: d.orderId,
    reason: d.reason,
    ...(d.refundFailed === true ? { refundFailed: true } : {}),
    ...(typeof d.refundFailureReason === 'string'
      ? { refundFailureReason: d.refundFailureReason }
      : {}),
  };
}

/** M05: the read-only refusal and the grown-up who CAN act, else null. */
export function readOnlyOf(e: unknown): ReadOnlyDetails | null {
  const d = detailsOf(e, 'FORBIDDEN');
  if (!d || d.readOnly !== true) return null;
  return { readOnly: true, ...(typeof d.canAct === 'string' ? { canAct: d.canAct } : {}) };
}

/** M02: the hard lock behind a refusal, with the counts M02 reads back, else null. */
export function planLockedOf(e: unknown): PlanLockedDetails | null {
  const d = detailsOf(e, 'PLAN_LIMIT');
  if (!d || d.locked !== true) return null;
  const num = (v: unknown): number | undefined => (typeof v === 'number' ? v : undefined);
  return {
    locked: true,
    ...(num(d.children) !== undefined ? { children: num(d.children) } : {}),
    ...(num(d.childLimit) !== undefined ? { childLimit: num(d.childLimit) } : {}),
    ...(num(d.grownups) !== undefined ? { grownups: num(d.grownups) } : {}),
    ...(num(d.grownupLimit) !== undefined ? { grownupLimit: num(d.grownupLimit) } : {}),
    ...(num(d.chooseChildren) !== undefined ? { chooseChildren: num(d.chooseChildren) } : {}),
  };
}
